import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  LICENSE_CLASSES,
  LICENSE_STATUSES,
  useCreateTechnicianLicense,
  useUpdateTechnicianLicense,
  type LicenseClass,
  type LicenseStatus,
  type TechnicianLicense,
} from '@/routes/technicians/useTechnicianLicenses';

/**
 * Create-or-edit one licence (M1-37) — owner/admin only, enforced server-side
 * by `users.manage`. One dialog for both, the way
 * {@link import('./TechnicianProfileFormDialog').TechnicianProfileFormDialog}
 * is: the API takes the same body for POST and PUT, because a licence's parent
 * profile lives in the route rather than the payload.
 *
 * <b>The "what is it?" field appears only for class Other</b>, mirroring the
 * API's own rule (required for Other, rejected otherwise) rather than sending
 * a field the server will refuse. Same shape as the profile form's address
 * block, which only renders for a Home-based technician.
 */
export function TechnicianLicenseDialog({
  profileId,
  technicianName,
  license,
  onClose,
  onSaved,
}: {
  profileId: string;
  technicianName: string;
  /** Absent for create. */
  license?: TechnicianLicense;
  onClose: () => void;
  onSaved: (announcement: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateTechnicianLicense(profileId);
  // Called unconditionally (Rules of Hooks) with an id that is never used in
  // create mode — `submit` dispatches to `create` when there is no licence.
  const update = useUpdateTechnicianLicense(profileId, license?.id ?? '');
  const mutation = license ? update : create;

  const [licenseClass, setLicenseClass] = useState<LicenseClass>(
    (license?.class as LicenseClass | undefined) ?? 'Journeyman',
  );
  const [classOtherLabel, setClassOtherLabel] = useState(license?.classOtherLabel ?? '');
  const [issuingJurisdiction, setIssuingJurisdiction] = useState(
    license?.issuingJurisdiction ?? '',
  );
  const [number, setNumber] = useState(license?.number ?? '');
  const [issuedOn, setIssuedOn] = useState(license?.issuedOn ?? '');
  const [expiresOn, setExpiresOn] = useState(license?.expiresOn ?? '');
  const [status, setStatus] = useState<LicenseStatus>(
    (license?.status as LicenseStatus | undefined) ?? 'Active',
  );
  const [notes, setNotes] = useState(license?.notes ?? '');

  const otherLabelMissing = licenseClass === 'Other' && classOtherLabel.trim() === '';
  // Plain string comparison, deliberately: both are ISO yyyy-MM-dd from a
  // <input type="date">, which sorts lexicographically — and putting them
  // through `new Date()` to compare would drag in the UTC-midnight timezone
  // trap this file's siblings already document for `hireDate`.
  const datesBackwards = issuedOn !== '' && expiresOn !== '' && expiresOn < issuedOn;
  const canSubmit = !otherLabelMissing && !datesBackwards;

  function submit() {
    if (!canSubmit) {
      return;
    }

    mutation.mutate(
      {
        class: licenseClass,
        // Never sent for a named class — the API rejects a labelled
        // Journeyman, and so it should: the row would contradict itself.
        classOtherLabel: licenseClass === 'Other' ? classOtherLabel.trim() : null,
        issuingJurisdiction: issuingJurisdiction.trim() || null,
        number: number.trim() || null,
        issuedOn: issuedOn || null,
        expiresOn: expiresOn || null,
        status,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          onSaved(
            license
              ? t('technicianLicenses.form.saved')
              : t('technicianLicenses.form.created', { name: technicianName }),
          );
          onClose();
        },
      },
    );
  }

  return (
    <Dialog
      titleId="technician-license-title"
      title={
        license
          ? t('technicianLicenses.form.editTitle')
          : t('technicianLicenses.form.createTitle', { name: technicianName })
      }
      onClose={onClose}
      testId="technician-license-dialog"
      dismissLabel={t('technicianLicenses.form.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={mutation.isPending || !canSubmit}
            onClick={submit}
          >
            {mutation.isPending
              ? t('technicianLicenses.form.saving')
              : t('technicianLicenses.form.confirm')}
          </Button>
        </>
      }
    >
      {/*
        Explicit htmlFor/id, NOT a wrapping <label>. A <label> wrapped around a
        <select> folds every <option>'s text into the control's accessible name
        — "ClassApprenticeJourneymanMasterGas fitter…" — and a hint <span>
        inside the label joins the name too, so "Expires" became "ExpiresLeave
        blank if it doesn't expire.". Both are real screen-reader defects, and
        both are also why `getByRole('combobox', { name: 'Class' })` could not
        find these controls; the tests found the accessibility bug first.
      */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1 text-sm">
          <label htmlFor="license-class">{t('technicianLicenses.form.class')}</label>
          <select
            id="license-class"
            className={FIELD_CLASS}
            value={licenseClass}
            onChange={(event) => setLicenseClass(event.target.value as LicenseClass)}
          >
            {LICENSE_CLASSES.map((option) => (
              <option key={option} value={option}>
                {t(`technicianLicenses.class.${option}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-1 text-sm">
          <label htmlFor="license-status">{t('technicianLicenses.form.status')}</label>
          <select
            id="license-status"
            className={FIELD_CLASS}
            value={status}
            onChange={(event) => setStatus(event.target.value as LicenseStatus)}
          >
            {LICENSE_STATUSES.map((option) => (
              <option key={option} value={option}>
                {t(`technicianLicenses.status.${option}`)}
              </option>
            ))}
          </select>
        </div>

        {licenseClass === 'Other' ? (
          <div className="grid gap-1 text-sm sm:col-span-2">
            <label htmlFor="license-other-label">
              {t('technicianLicenses.form.classOtherLabel')}
            </label>
            <input
              id="license-other-label"
              className={FIELD_CLASS}
              value={classOtherLabel}
              maxLength={200}
              aria-describedby="license-other-hint"
              onChange={(event) => setClassOtherLabel(event.target.value)}
            />
            <span id="license-other-hint" className="text-xs text-[var(--color-muted-foreground)]">
              {t('technicianLicenses.form.classOtherHint')}
            </span>
          </div>
        ) : null}

        <div className="grid gap-1 text-sm">
          <label htmlFor="license-jurisdiction">
            {t('technicianLicenses.form.issuingJurisdiction')}
          </label>
          <input
            id="license-jurisdiction"
            className={FIELD_CLASS}
            value={issuingJurisdiction}
            maxLength={100}
            onChange={(event) => setIssuingJurisdiction(event.target.value)}
          />
        </div>

        <div className="grid gap-1 text-sm">
          <label htmlFor="license-number">{t('technicianLicenses.form.number')}</label>
          <input
            id="license-number"
            className={FIELD_CLASS}
            value={number}
            maxLength={100}
            onChange={(event) => setNumber(event.target.value)}
          />
        </div>

        <div className="grid gap-1 text-sm">
          <label htmlFor="license-issued-on">{t('technicianLicenses.form.issuedOn')}</label>
          <input
            id="license-issued-on"
            type="date"
            className={FIELD_CLASS}
            value={issuedOn}
            onChange={(event) => setIssuedOn(event.target.value)}
          />
        </div>

        <div className="grid gap-1 text-sm">
          <label htmlFor="license-expires-on">{t('technicianLicenses.form.expiresOn')}</label>
          <input
            id="license-expires-on"
            type="date"
            className={FIELD_CLASS}
            value={expiresOn}
            aria-describedby="license-expires-hint"
            onChange={(event) => setExpiresOn(event.target.value)}
          />
          <span id="license-expires-hint" className="text-xs text-[var(--color-muted-foreground)]">
            {t('technicianLicenses.form.expiresOnHint')}
          </span>
        </div>

        <div className="grid gap-1 text-sm sm:col-span-2">
          <label htmlFor="license-notes">{t('technicianLicenses.form.notes')}</label>
          <textarea
            id="license-notes"
            className={FIELD_CLASS}
            value={notes}
            rows={2}
            maxLength={2000}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      </div>

      {/* The two client-side refusals, said out loud rather than left to a
          greyed-out button the user has to guess about. `role="alert"` so a
          screen-reader user hears why Save stopped working. */}
      {datesBackwards ? (
        <p role="alert" className="mt-2 text-sm text-[var(--color-destructive)]">
          {t('technicianLicenses.form.datesBackwards')}
        </p>
      ) : null}
      {mutation.isError ? (
        <p role="alert" className="mt-2 text-sm text-[var(--color-destructive)]">
          {t('technicianLicenses.form.failed')}
        </p>
      ) : null}
    </Dialog>
  );
}
